import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

const testimonials = [
  {
    quote: "After following the stretches for one week, my lower back stiffness reduced significantly. I can finally sit through long meetings without pain.",
    name: "Priya S.",
    role: "Software Engineer, Bengaluru",
    img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop",
  },
  {
    quote: "I sit 10 hours daily for work. This PDF helped me fix my posture and reduce pain in just 10 days. Best ₹199 I've ever spent.",
    name: "Amit R.",
    role: "Accountant, Mumbai",
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop",
  },
  {
    quote: "Simple exercises but very effective. My morning back stiffness is almost gone. Doctor explains everything clearly.",
    name: "Neha K.",
    role: "Teacher, Delhi",
    img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop",
  },
  {
    quote: "At 62, I was worried about doing exercises. But this guide is gentle and senior-friendly. My sciatica pain has reduced by 70%.",
    name: "Rajesh M.",
    role: "Retired, Pune",
    img: "https://images.unsplash.com/photo-1559548331-f9cb98280344?w=200&h=200&fit=crop",
  },
];

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const len = testimonials.length;

  const prev = () => setIndex((i) => (i - 1 + len) % len);
  const next = () => setIndex((i) => (i + 1) % len);

  return (
    <section id="testimonials" className="py-20 md:py-28 bg-white" data-testid="testimonials-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">
            Real Stories
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1E3A8A] leading-tight" style={{ fontFamily: "Outfit" }}>
            Loved by patients across India
          </h2>
        </div>

        <div className="relative">
          <div className="relative bg-gradient-to-br from-blue-50 via-white to-emerald-50 rounded-3xl p-6 sm:p-12 border border-slate-100 shadow-xl shadow-blue-900/5 overflow-hidden">
            <div className="absolute top-4 right-4 sm:top-8 sm:right-8 text-blue-100 text-7xl sm:text-9xl font-black leading-none select-none pointer-events-none" style={{ fontFamily: "Outfit" }}>
              "
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4 }}
                className="relative"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />
                  ))}
                </div>

                <p className="text-lg sm:text-2xl text-slate-800 leading-relaxed font-medium mb-8" style={{ fontFamily: "Outfit" }}>
                  "{testimonials[index].quote}"
                </p>

                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-blue-100 ring-2 ring-white shadow-md shrink-0">
                    <img src={testimonials[index].img} alt={testimonials[index].name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="font-bold text-[#1E3A8A] text-base" style={{ fontFamily: "Outfit" }}>
                      {testimonials[index].name}
                    </div>
                    <div className="text-sm text-slate-600">{testimonials[index].role}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between mt-6">
            <div className="flex gap-2" data-testid="testimonial-dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  className={`h-2 rounded-full transition-all ${
                    i === index ? "w-8 bg-[#1E3A8A]" : "w-2 bg-slate-300 hover:bg-slate-400"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                  data-testid={`testimonial-dot-${i}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={prev}
                className="w-11 h-11 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 hover:border-[#1E3A8A] transition-all"
                aria-label="Previous testimonial"
                data-testid="testimonial-prev"
              >
                <ChevronLeft className="w-5 h-5 text-slate-700" />
              </button>
              <button
                onClick={next}
                className="w-11 h-11 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center hover:bg-blue-900 transition-all"
                aria-label="Next testimonial"
                data-testid="testimonial-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
